import { useEffect, useState, type ReactNode } from 'react'
import { useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react'
import { Placeholder } from '@tiptap/extensions'
import { emailExtensions } from './editorSchema'
import './EmailEditor.css'

function normalizeUrl(raw: string) {
  const url = raw.trim()
  if (!url) return ''
  if (/^(https?:\/\/|mailto:)/i.test(url)) return url
  if (url.includes('@') && !url.includes('/')) return `mailto:${url}`
  return `https://${url}`
}

type Panel = { kind: 'link' } | { kind: 'button' } | { kind: 'image' } | null

interface EmailEditorProps {
  value: string
  onChange: (html: string) => void
  disabled?: boolean
}

export function EmailEditor({ value, onChange, disabled = false }: EmailEditorProps) {
  const [panel, setPanel] = useState<Panel>(null)

  const editor = useEditor({
    extensions: [
      ...emailExtensions,
      Placeholder.configure({ placeholder: 'Write your email here. Use the toolbar for headings, links, buttons and images.' }),
    ],
    content: value,
    editable: !disabled,
    editorProps: { attributes: { class: 'email-editor-content', 'aria-label': 'Email message' } },
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? '' : editor.getHTML()),
  })

  useEffect(() => {
    editor?.setEditable(!disabled, false)
  }, [editor, disabled])

  if (!editor) return null

  return (
    <div className={`email-editor rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] focus-within:border-[#503864] dark:focus-within:border-[#7f4f9a] focus-within:ring-2 focus-within:ring-[#503864]/20 ${disabled ? 'opacity-60' : ''}`}>
      <Toolbar editor={editor} panel={panel} setPanel={setPanel} disabled={disabled} />
      {panel && <InsertPanel editor={editor} panel={panel} onDone={() => setPanel(null)} />}
      <EditorContent editor={editor} />
    </div>
  )
}

function Toolbar({
  editor,
  panel,
  setPanel,
  disabled,
}: {
  editor: Editor
  panel: Panel
  setPanel: (p: Panel) => void
  disabled: boolean
}) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      link: e.isActive('link'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      center: e.isActive({ textAlign: 'center' }),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  })

  const chain = () => editor.chain().focus()
  const toggle = (p: NonNullable<Panel>['kind']) => setPanel(panel?.kind === p ? null : { kind: p })

  return (
    <div className="flex flex-wrap items-center gap-px border-b border-gray-200 dark:border-[#3d2c4f] px-1.5 py-1.5">
      <Tool label="Big heading" active={state.h1} disabled={disabled} onClick={() => chain().toggleHeading({ level: 1 }).run()}>
        <span className="font-heading text-[15px] font-bold">H1</span>
      </Tool>
      <Tool label="Heading" active={state.h2} disabled={disabled} onClick={() => chain().toggleHeading({ level: 2 }).run()}>
        <span className="font-heading text-[13px] font-bold">H2</span>
      </Tool>
      <Divider />
      <Tool label="Bold" active={state.bold} disabled={disabled} onClick={() => chain().toggleBold().run()}>
        <span className="font-bold">B</span>
      </Tool>
      <Tool label="Italic" active={state.italic} disabled={disabled} onClick={() => chain().toggleItalic().run()}>
        <span className="font-serif italic">I</span>
      </Tool>
      <Tool label="Underline" active={state.underline} disabled={disabled} onClick={() => chain().toggleUnderline().run()}>
        <span className="underline">U</span>
      </Tool>
      <Divider />
      <Tool label="Bulleted list" active={state.bullet} disabled={disabled} onClick={() => chain().toggleBulletList().run()}>
        <Icon d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
      </Tool>
      <Tool label="Numbered list" active={state.ordered} disabled={disabled} onClick={() => chain().toggleOrderedList().run()}>
        <Icon d="M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
      </Tool>
      <Tool label="Quote" active={state.quote} disabled={disabled} onClick={() => chain().toggleBlockquote().run()}>
        <Icon d="M7 7h4v4H8c0 2 1 3 3 3v2c-3 0-5-2-5-5V7zm8 0h4v4h-3c0 2 1 3 3 3v2c-3 0-5-2-5-5V7z" />
      </Tool>
      <Tool
        label="Center"
        active={state.center}
        disabled={disabled}
        onClick={() => (state.center ? chain().unsetTextAlign().run() : chain().setTextAlign('center').run())}
      >
        <Icon d="M4 6h16M7 12h10M5 18h14" />
      </Tool>
      <Divider />
      <Tool label="Link" active={state.link || panel?.kind === 'link'} disabled={disabled} onClick={() => toggle('link')}>
        <Icon d="M10 14a4 4 0 005.66 0l3-3a4 4 0 00-5.66-5.66l-1 1M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l1-1" />
      </Tool>
      <Tool label="Button" active={panel?.kind === 'button'} disabled={disabled} onClick={() => toggle('button')}>
        <span className="rounded-full bg-[#503864] px-2 py-0.5 text-[10px] font-semibold text-white dark:bg-[#9a6bb8]">Button</span>
      </Tool>
      <Tool label="Image" active={panel?.kind === 'image'} disabled={disabled} onClick={() => toggle('image')}>
        <Icon d="M4 16l4.6-4.6a2 2 0 012.8 0L16 16m-2-2l1.6-1.6a2 2 0 012.8 0L20 14M14 8h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </Tool>
      <Tool label="Divider line" disabled={disabled} onClick={() => chain().setHorizontalRule().run()}>
        <Icon d="M4 12h16" />
      </Tool>
      <div className="ml-auto flex items-center gap-0.5">
        <Tool label="Undo" disabled={disabled || !state.canUndo} onClick={() => chain().undo().run()}>
          <Icon d="M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 010 11H11" />
        </Tool>
        <Tool label="Redo" disabled={disabled || !state.canRedo} onClick={() => chain().redo().run()}>
          <Icon d="M15 14l5-5-5-5M20 9H9.5a5.5 5.5 0 000 11H13" />
        </Tool>
      </div>
    </div>
  )
}

function InsertPanel({ editor, panel, onDone }: { editor: Editor; panel: NonNullable<Panel>; onDone: () => void }) {
  const existingHref = (editor.getAttributes('link').href as string | undefined) ?? ''
  const selectedText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to, ' ')
  const [url, setUrl] = useState(panel.kind === 'link' ? existingHref : '')
  const [text, setText] = useState(panel.kind === 'button' ? selectedText : '')

  const apply = () => {
    const href = normalizeUrl(url)
    const chain = editor.chain().focus()
    if (panel.kind === 'link') {
      if (!href) chain.extendMarkRange('link').unsetLink().run()
      else if (editor.state.selection.empty && !editor.isActive('link'))
        chain.insertContent({ type: 'text', text: url.trim(), marks: [{ type: 'link', attrs: { href } }] }).run()
      else chain.extendMarkRange('link').setLink({ href }).run()
    } else if (panel.kind === 'button') {
      if (!href || !text.trim()) return
      chain
        .insertContent({
          type: 'paragraph',
          attrs: { textAlign: 'center' },
          content: [
            { type: 'text', text: text.trim(), marks: [{ type: 'link', attrs: { href, button: true } }, { type: 'bold' }] },
          ],
        })
        .run()
    } else {
      if (!href) return
      chain.setImage({ src: href }).run()
    }
    onDone()
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      apply()
    } else if (e.key === 'Escape') {
      onDone()
    }
  }

  const inputClass =
    'min-w-0 flex-1 rounded-lg border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:border-[#503864] focus:outline-none'

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-[#3d2c4f] bg-gray-50 dark:bg-white/5 px-3 py-2">
      {panel.kind === 'button' && (
        <input autoFocus className={inputClass} placeholder="Button text, e.g. Shop the drop" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
      )}
      <input
        autoFocus={panel.kind !== 'button'}
        className={inputClass}
        placeholder={panel.kind === 'image' ? 'Image URL (https://media.bartacdn.net/...)' : 'Link, e.g. barta.shop/events'}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={onKey}
      />
      <button type="button" onClick={apply} className="rounded-lg bg-[#7f4f9a] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#6b4282] dark:bg-[#9a6bb8]">
        {panel.kind === 'link' && existingHref ? 'Update' : 'Insert'}
      </button>
      {panel.kind === 'link' && existingHref && (
        <button
          type="button"
          onClick={() => {
            editor.chain().focus().extendMarkRange('link').unsetLink().run()
            onDone()
          }}
          className="rounded-lg px-2 py-1.5 text-sm text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-white/10"
        >
          Remove link
        </button>
      )}
      <button type="button" onClick={onDone} className="rounded-lg px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-200 dark:hover:bg-white/10">
        Cancel
      </button>
    </div>
  )
}

function Tool({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`flex h-8 min-w-7 items-center justify-center rounded-md px-1 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-35 ${
        active
          ? 'bg-[#503864]/10 text-[#503864] dark:bg-[#9a6bb8]/20 dark:text-[#d6b8ea]'
          : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10'
      }`}
    >
      {children}
    </button>
  )
}

function Icon({ d }: { d: string }) {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-gray-200 dark:bg-[#3d2c4f]" />
}
